/**
 * Built-in developmental framework data — mathematics, fine motor, gross motor.
 *
 * Level descriptors are written so a parent can verify them in the moment:
 * every one names a behavior you can watch happen, not a capacity you have to infer.
 * Where the rubric records a real observation of Aurora, that observation is the exemplar.
 *
 * Research anchors used here:
 *  - Gelman & Gallistel's counting principles (stable order, one-to-one, cardinality)
 *  - Piaget on seriation, classification and conservation of number
 *  - Sarama & Clements learning trajectories for counting, shape and pattern
 *  - Lowenfeld's stages of artistic development (scribbling → preschematic → schematic)
 *  - Gentry's spelling stages (precommunicative → semiphonetic → phonetic → transitional)
 *  - Schneck & Henderson's pencil grasp progression
 *  - Clark & Metcalfe's mountain of motor development for throwing, catching and hopping
 *  - Vygotsky: every level is written as the next reachable step, not a fixed ceiling
 *
 * Everything in this file is data. No I/O, no clock, no randomness.
 */

import type {
  DomainDef,
  DomainId,
  DomainKey,
  FrameworkId,
  Skill,
  SkillId,
} from '../types'

// ─────────────────────────────────────────────────────────────
// Id construction
// ─────────────────────────────────────────────────────────────

/** The single built-in framework every domain in this file belongs to. */
const FRAMEWORK_ID = 'framework.aurora.builtin.v1' as FrameworkId

const domainId = (key: DomainKey): DomainId => `domain.${key}` as DomainId

/** Skill ids are `${domainKey}.${key}` so an id is readable in a log line. */
const sid = (domainKey: DomainKey, key: string): SkillId =>
  `${domainKey}.${key}` as SkillId

// ─────────────────────────────────────────────────────────────
// Domains
// ─────────────────────────────────────────────────────────────

export const mathDomain: DomainDef = {
  _id: domainId('math'),
  frameworkId: FRAMEWORK_ID,
  key: 'math',
  name: 'Mathematics',
  description:
    'Number, quantity and space. Saying the count, matching one number to one object, comparing amounts, sorting, patterns, shapes, and the first joining, taking away and sharing.',
  colorToken: 'domain.math',
  iconKey: 'calculator',
  sortOrder: 4,
}

export const fineMotorDomain: DomainDef = {
  _id: domainId('fineMotor'),
  frameworkId: FRAMEWORK_ID,
  key: 'fineMotor',
  name: 'Fine Motor & Pre-Writing',
  description:
    'The hands and the pencil. Grip, cutting, threading, drawing that stands for real things, and the letters and invented spellings that grow out of drawing.',
  colorToken: 'domain.fineMotor',
  iconKey: 'pencil-line',
  sortOrder: 5,
}

export const grossMotorDomain: DomainDef = {
  _id: domainId('grossMotor'),
  frameworkId: FRAMEWORK_ID,
  key: 'grossMotor',
  name: 'Gross Motor & Physical',
  description:
    'The whole body in motion. Jumping, balance, climbing, riding and ball skills — including catching, which is a matter of hand timing rather than strength.',
  colorToken: 'domain.grossMotor',
  iconKey: 'person-standing',
  sortOrder: 6,
}

export const mathMotorDomains: DomainDef[] = [
  mathDomain,
  fineMotorDomain,
  grossMotorDomain,
]

// ─────────────────────────────────────────────────────────────
// Mathematics
// ─────────────────────────────────────────────────────────────

const M = (key: string): SkillId => sid('math', key)

export const mathSkills: Skill[] = [
  {
    _id: M('counting'),
    domainId: mathDomain._id,
    domainKey: 'math',
    key: 'counting',
    name: 'Rote Counting',
    description:
      'Saying the number words in a fixed order — the verbal chant on its own. This is a memory sequence, and it is a different skill from counting a set of objects.',
    levelDescriptors: [
      {
        level: 1,
        descriptor:
          'Says a few number words as a chant with the order not yet fixed — "one, two, five, eight" — and restarts at one when she loses the thread.',
        exemplars: [
          'Chants "one two three" while stomping upstairs, then jumps to "eight"',
          'Joins in on the last number of a counting song rather than leading it',
        ],
      },
      {
        level: 2,
        descriptor:
          'Says one to ten in the same order most times and starts the chant herself, dropping a number here and there.',
        exemplars: [
          'Counts the stairs to ten going up, missing seven about half the time',
          'Counts to ten to start a game of hide and seek',
        ],
      },
      {
        level: 3,
        descriptor:
          'Says one to twenty in order with no model. The teen words are mostly right, with an occasional slip like "fourteen, sixteen".',
        exemplars: [
          'Counted to about eighteen unprompted during Cycle 1',
          'Chants the numbers to twenty while bouncing, without an adult starting her off',
        ],
      },
      {
        level: 4,
        descriptor:
          'Counts to thirty or beyond and crosses each decade on her own (twenty-nine into thirty), and can pick up the count from a number you name — "start at seven".',
        exemplars: [
          'Counts to thirty-nine, pauses at the decade, then finds "forty"',
          'Counts on from twelve when you say "start at twelve" instead of going back to one',
        ],
      },
      {
        level: 5,
        descriptor:
          'Counts past fifty, counts by tens to one hundred, and counts backwards from ten to one.',
        exemplars: [
          'Counts backwards from ten for a rocket launch and says "blast off" on zero',
          'Counts by tens along the fence posts on a walk',
        ],
      },
    ],
    typicalMonths: [30, 36],
    prerequisites: [],
    assessmentHints: [
      'Count stairs or fence posts together, then go quiet halfway up and hear how far she carries the chant alone.',
      'Ask her to start at a number in the middle, like "start at seven". If the chant is one fixed song she will go back to one.',
      'Play rocket launches and see whether the numbers run backwards as easily as forwards.',
    ],
    sortOrder: 1,
  },
  {
    _id: M('correspondence'),
    domainId: mathDomain._id,
    domainKey: 'math',
    key: 'correspondence',
    name: 'One-to-One Correspondence',
    description:
      'Giving exactly one number word to each object, and knowing that the last word said is how many there are. The chant becomes a count of real things.',
    levelDescriptors: [
      {
        level: 1,
        descriptor:
          'Points along a row while saying number words, but the pointing and the words run at different speeds — four words over two objects.',
        exemplars: [
          'Touches two blocks and says "one, two, three, four"',
          'Sweeps a hand over the whole plate and says "five"',
        ],
      },
      {
        level: 2,
        descriptor:
          'Counts up to five objects accurately when they sit in a line, touching each one as she says its number.',
        exemplars: [
          'Counts five crackers on the plate, tapping each one',
          'Counts the four wheels on a toy car by walking a finger around it',
        ],
      },
      {
        level: 3,
        descriptor:
          'Counts up to ten objects accurately by touching or moving each one, and answers "how many?" with the last number instead of counting all over again.',
        exemplars: [
          'One-to-one correspondence solid to about ten at 2.75 years — points and counts',
          'Counts the legs on a bug during a bug hunt and answers "six" when asked how many',
        ],
      },
      {
        level: 4,
        descriptor:
          'Counts fifteen to twenty scattered objects by organizing them — lining them up, or pushing counted ones aside — and names groups of three or four at a glance without counting.',
        exemplars: [
          'Slides each counted bead to the left so none gets counted twice',
          'Says "three!" the moment three dice pips land, with no finger pointing',
        ],
      },
      {
        level: 5,
        descriptor:
          'Counts twenty or more accurately, counts things she cannot touch or move (dots on a page, birds on a wire), and says the total stays the same when you spread the same objects further apart.',
        exemplars: [
          'Counts a bunched-up row and a spread-out row and says both are still eight',
          'Counts the windows on a building from the pavement without pointing',
        ],
      },
    ],
    typicalMonths: [33, 42],
    prerequisites: [M('counting')],
    assessmentHints: [
      'Put out a handful of snacks and ask "how many?". Listen for whether she answers with the last number or starts the whole count again.',
      'Scatter ten small objects instead of lining them up, and watch whether she invents a way to keep track.',
      'Ask her to fetch exactly four spoons for the table. Counting to get a set number is harder than counting a set already in front of her.',
    ],
    sortOrder: 2,
  },
  {
    _id: M('numeralRecognition'),
    domainId: mathDomain._id,
    domainKey: 'math',
    key: 'numeralRecognition',
    name: 'Numeral Recognition',
    description:
      'Reading the written number symbols and connecting a numeral to an amount. Numerals are a second code on top of the spoken count.',
    levelDescriptors: [
      {
        level: 1,
        descriptor:
          'Notices numerals as a special kind of mark and points them out — on a clock, a door, a birthday candle — without naming them.',
        exemplars: [
          'Points at the numbers on the microwave and says "those are numbers"',
          'Picks out the price tag as "the number part"',
        ],
      },
      {
        level: 2,
        descriptor:
          'Names 1, 2 and 3 on sight, and finds the numeral for her own age in a pile of others.',
        exemplars: [
          'Picks the foam 2 out of the bath letters because "that\'s how old I am"',
          'Names the 1 on the lift button',
        ],
      },
      {
        level: 3,
        descriptor:
          'Names the numerals 1 to 10 out of order, and puts the right number of objects onto a card showing a numeral.',
        exemplars: [
          'Deals four blocks onto the card marked 4',
          'Reads the numerals on the lift panel and presses the right floor',
        ],
      },
      {
        level: 4,
        descriptor:
          'Names numerals to twenty, writes several of them from memory (some reversed, which is expected at this age), and finds a number in the wild — a page number, a house number, a clock face.',
        exemplars: [
          'Numbers the pages of her own book 1 to 5',
          'Reads the 12 on the paper clock and says that is lunchtime',
        ],
      },
      {
        level: 5,
        descriptor:
          'Reads two-digit numbers, lays numeral cards 1 to 20 in order, and says which of two written numbers is more without counting anything out.',
        exemplars: [
          'Orders the numeral cards, then slots a missed card into the right gap',
          'Says 14 is more than 9 straight from the written numbers',
        ],
      },
    ],
    typicalMonths: [36, 48],
    prerequisites: [M('counting')],
    assessmentHints: [
      'Lift buttons, house numbers on a walk and page numbers in a book are free daily probes — ask "what number is that?" once, then leave it.',
      'Deal a card with a numeral on it and a pile of counters, and ask her to give that many. That tests the link between the symbol and the amount, not just naming.',
      'Watch her own book-making: page numbers she writes unprompted show which numerals she owns.',
    ],
    sortOrder: 3,
  },
  {
    _id: M('comparison'),
    domainId: mathDomain._id,
    domainKey: 'math',
    key: 'comparison',
    name: 'Comparing & Ordering',
    description:
      'Judging more and less, bigger and smaller, longer and shorter — then finding how many more, and putting several things in order along one attribute (seriation).',
    levelDescriptors: [
      {
        level: 1,
        descriptor:
          'Reaches for the bigger of two clearly different things and for the plate that visibly has more.',
        exemplars: [
          'Takes the bigger half of the banana every single time',
          'Chooses the taller cup of juice without being asked to compare',
        ],
      },
      {
        level: 2,
        descriptor:
          'Uses "more", "less", "big" and "small" correctly about two things sitting side by side.',
        exemplars: [
          '"You have more grapes than me"',
          'Calls the two towers "the big one" and "the little one"',
        ],
      },
      {
        level: 3,
        descriptor:
          'Says which of two groups has more even when the pieces are different sizes, and uses bigger/smaller, longer/shorter and heavier/lighter about a matched pair.',
        exemplars: [
          'More/less and bigger/smaller mastered by 2.75 years',
          'Says the row of six small buttons is more than the row of four big ones',
        ],
      },
      {
        level: 4,
        descriptor:
          'Answers "how many more?" for small groups by counting both and finding the gap, and puts three or more things in order from smallest to biggest.',
        exemplars: [
          'Counts five plates and three chairs and says "you need two more"',
          'Lines up five nesting cups smallest to biggest without trial and error',
        ],
      },
      {
        level: 5,
        descriptor:
          'Slots a new item into an already ordered row in the right place, and re-orders the same set along a different attribute when asked — "now by how heavy, not how big".',
        exemplars: [
          'Puts a sixth cup into the middle of the ordered line without rebuilding the row',
          'Orders five stones by weight after ordering them by size',
        ],
      },
    ],
    typicalMonths: [30, 36],
    prerequisites: [],
    assessmentHints: [
      'Share out a snack unevenly on purpose and wait. The complaint tells you what she can compare.',
      'Hand her the nesting cups or measuring spoons out of order and see whether she builds the ladder or just stacks them.',
      'At the table, ask "how many more forks do we need?" — that is the difference question hiding inside a real job.',
    ],
    sortOrder: 4,
  },
  {
    _id: M('sorting1Attribute'),
    domainId: mathDomain._id,
    domainKey: 'math',
    key: 'sorting1Attribute',
    name: 'Sorting by One Attribute',
    description:
      'Putting a mixed group into piles that share one property — all the red ones, all the round ones — and saying what the rule was.',
    levelDescriptors: [
      {
        level: 1,
        descriptor:
          'Matches two identical things and hands you the one that goes with the one you are holding.',
        exemplars: [
          'Finds the other red block when you hold one up',
          'Puts the matching sock on top of its pair',
        ],
      },
      {
        level: 2,
        descriptor:
          'Sorts a mixed set into two piles by an obvious property once you have started each pile for her.',
        exemplars: [
          'Puts red bears with red and blue with blue after you seed both piles',
          'Adds spoons to the spoon pile and forks to the fork pile while emptying the dishwasher',
        ],
      },
      {
        level: 3,
        descriptor:
          'Sorts a mixed set into piles by one property with no pile started for her, and says the rule she used.',
        exemplars: [
          'Single-attribute sorting mastered by 2.5 years — by colour, or by shape',
          'Sorted a nature-walk basket and said "these are the scratchy ones"',
        ],
      },
      {
        level: 4,
        descriptor:
          'Sorts the same set again by a different rule when asked — colour first, then shape — and names both rules.',
        exemplars: [
          'Sorts animal cards by where they live, then re-sorts the same cards by how many legs',
          'Sorts the crayons by colour, then by how short they are',
        ],
      },
      {
        level: 5,
        descriptor:
          'Invents her own rule for an unfamiliar set and explains it, including rules you cannot see — living and not living, floats and sinks.',
        exemplars: [
          'Sorted the nature walk into "alive" and "not alive" and defended the tricky ones',
          'Sorted toys into "things with wheels" and "things without" without being given the idea',
        ],
      },
    ],
    typicalMonths: [30, 36],
    prerequisites: [],
    assessmentHints: [
      'Cleanup is a free sorting probe. Say "everything goes away" and watch whether piles appear on their own.',
      'Tip out a mixed basket — buttons, blocks, shells — and just say "can you put these into groups?" without naming a rule.',
      'After she sorts, ask "how did you decide?". Naming the rule is the real skill; the piles are only the evidence.',
    ],
    sortOrder: 5,
  },
  {
    _id: M('patterns'),
    domainId: mathDomain._id,
    domainKey: 'math',
    key: 'patterns',
    name: 'Patterns',
    description:
      'Seeing, continuing and building repeating patterns — AB, AAB, ABC — in objects, in sounds and in movement. Pattern is where algebra starts.',
    levelDescriptors: [
      {
        level: 1,
        descriptor:
          'Notices a repeat and joins in with the rhythm after a few rounds, without predicting what comes next.',
        exemplars: [
          'Claps along to a clap-clap-stomp rhythm once it has run a few times',
          'Joins the repeated line of a song at the right moment',
        ],
      },
      {
        level: 2,
        descriptor:
          'Copies an AB pattern that is sitting in front of her by matching item to item underneath it.',
        exemplars: [
          'Copies red-blue-red-blue by putting each bead directly under yours',
          'Copies a stamp pattern of leaf-circle-leaf-circle',
        ],
      },
      {
        level: 3,
        descriptor:
          'Continues an AB pattern of six or more items with no model, and calls out what comes next before she places it.',
        exemplars: [
          'AB and AAB patterns reliable about four times in five at 2.75 years',
          'Threads a bead string and says "red!" before reaching for the red bead',
        ],
      },
      {
        level: 4,
        descriptor:
          'Continues AAB and ABC patterns, and builds a repeating pattern of her own and says what its rule is.',
        exemplars: [
          'ABC patterns emerging at 2.75 years — three-element repeats',
          'Made a fruit kabob in a strawberry-strawberry-grape repeat and named the rule',
          'Body percussion in an ABC pattern: clap, stomp, tap',
        ],
      },
      {
        level: 5,
        descriptor:
          'Finds and fixes the mistake in someone else\'s pattern, moves a pattern from one medium to another (claps become blocks), and extends a growing pattern such as towers of one, two, then three blocks.',
        exemplars: [
          'Spots the missing beat in your clapping pattern and says exactly where the gap is',
          'Turns a clap-clap-stomp rhythm into a row of blocks with the same shape',
        ],
      },
    ],
    typicalMonths: [36, 48],
    prerequisites: [M('sorting1Attribute')],
    assessmentHints: [
      'Start a bead, block or snack pattern and simply stop. What she does with the silence is the assessment.',
      'Clap a pattern rather than laying one out. Sound patterns show whether she has the idea or only the visual match.',
      'Ask her to make one for you and then copy it deliberately wrong. Catching your error is a level above making her own.',
    ],
    sortOrder: 6,
  },
  {
    _id: M('sorting2Attributes'),
    domainId: mathDomain._id,
    domainKey: 'math',
    key: 'sorting2Attributes',
    name: 'Sorting by Two Attributes',
    description:
      'Holding two rules at once — the red circles kept apart from the blue circles. This is Piaget\'s multiple classification, and it usually lands inside a game long before it lands as a tidy-up task.',
    levelDescriptors: [
      {
        level: 1,
        descriptor:
          'Sorts by one property, notices the other partway through, and switches rules mid-stream so the piles end up mixed.',
        exemplars: [
          'Starts sorting by colour, spots the shapes, and finishes the pile by shape',
          'Puts all the big ones together, then quietly starts a red pile too',
        ],
      },
      {
        level: 2,
        descriptor:
          'Holds both properties when you have already fixed one of them for her — "these are all circles, now put the red circles here".',
        exemplars: [
          'Sorts red from blue once you have handed her only the circles',
          'Separates the big spoons from the little spoons once the forks are out of the way',
        ],
      },
      {
        level: 3,
        descriptor:
          'Sorts a mixed set into four groups by two properties at once inside a game she cares about, and names both rules when asked.',
        exemplars: [
          'Ran a doll clinic sorting patients by big/small AND spotty/plain',
          'Worked as a treasure hunt — "find everything that is both blue and round" — after the abstract pile-sorting version did not land',
        ],
      },
      {
        level: 4,
        descriptor:
          'Fills a two-by-two grid where the row carries one rule and the column carries another, and says why a piece belongs in its box.',
        exemplars: [
          'Places blocks in a grid with rows for size and columns for colour',
          'Explains "it goes here because it\'s big AND it\'s red"',
        ],
      },
      {
        level: 5,
        descriptor:
          'Sorts by three properties at once, or hunts out the one object in a big mixed set that satisfies two rules and rejects near misses.',
        exemplars: [
          'Finds the only small blue wooden bead in the whole box',
          'Sorts buttons by colour, size and number of holes at the same time',
        ],
      },
    ],
    typicalMonths: [42, 54],
    prerequisites: [M('sorting1Attribute')],
    assessmentHints: [
      'Make it a hunt, not a sort. "Find me everything that is both blue and round" reaches this skill when "put these into piles" does not.',
      'Give the sorting a job inside pretend play — a clinic sorting patients, a shop sorting stock — so the two rules serve a story.',
      'Hold up an item that meets one rule only and ask "does this one belong?". Rejecting a near miss is the clearest sign both rules are live at once.',
    ],
    sortOrder: 7,
  },
  {
    _id: M('shapes'),
    domainId: mathDomain._id,
    domainKey: 'math',
    key: 'shapes',
    name: 'Shapes',
    description:
      'Naming shapes, finding them in real objects, and using them as the building blocks of drawing and construction.',
    levelDescriptors: [
      {
        level: 1,
        descriptor:
          'Fits shapes into a shape sorter or a puzzle board by turning them until they drop through.',
        exemplars: [
          'Turns the triangle a few times until it slots into the board',
          'Posts the circle first because it goes in at any angle',
        ],
      },
      {
        level: 2,
        descriptor:
          'Names circle and square, and matches a shape to another of the same shape in a different colour or size.',
        exemplars: [
          'Calls every round thing a circle — plate, wheel, coin',
          'Finds the other square among mixed shape tiles',
        ],
      },
      {
        level: 3,
        descriptor:
          'Names circle, square, triangle and rectangle, and points them out in real objects around the room.',
        exemplars: [
          '"The window is a rectangle and the clock is a circle"',
          'Spots triangles in the roof line on a walk',
        ],
      },
      {
        level: 4,
        descriptor:
          'Also names oval, diamond, star and heart, counts the sides and corners of a shape, and builds a picture out of shapes.',
        exemplars: [
          'Draws a cat as circles plus oval body plus triangle ears after the shapes-as-building-blocks activity',
          'Counts three sides and three corners on a triangle by touching each one',
        ],
      },
      {
        level: 5,
        descriptor:
          'Still names a shape when it is rotated or stretched, names solid shapes (cube, sphere, cylinder), and says how two shapes differ.',
        exemplars: [
          'Calls a long thin triangle a triangle even though it looks nothing like the tile version',
          '"A square has all its sides the same, a rectangle has two long and two short"',
        ],
      },
    ],
    typicalMonths: [30, 40],
    prerequisites: [],
    assessmentHints: [
      'Run a shape hunt on a walk — one shape a day. Naming shapes on real objects is harder than naming plastic tiles.',
      'Cut her sandwich into a triangle and ask what you made, then cut it again and ask what you made now.',
      'When she draws, ask "what shapes is your cat made of?". That connects shape naming to how she actually uses shapes.',
    ],
    sortOrder: 8,
  },
  {
    _id: M('spatialReasoning'),
    domainId: mathDomain._id,
    domainKey: 'math',
    key: 'spatialReasoning',
    name: 'Spatial Reasoning',
    description:
      'Fitting, turning and arranging things in space — jigsaws, block structures, position words, and picturing a turn before making it.',
    levelDescriptors: [
      {
        level: 1,
        descriptor:
          'Completes an inset puzzle of four to six pieces where each piece has its own hole.',
        exemplars: [
          'Drops the farm animal pieces into their cutouts',
          'Stacks three graduated rings in the right order',
        ],
      },
      {
        level: 2,
        descriptor:
          'Completes an eight to twelve piece jigsaw with a bit of guidance, and turns a piece to make it fit instead of pressing harder.',
        exemplars: [
          'Rotates a piece a quarter turn and tries again rather than forcing it',
          'Builds the border first when you point at a corner piece',
        ],
      },
      {
        level: 3,
        descriptor:
          'Completes a twelve piece jigsaw on her own, and uses in, on, under, behind and next to correctly.',
        exemplars: [
          'Eight to twelve piece puzzles solid at 2.5 years',
          'Hides the bear "under the chair" and then "behind the door" on request',
        ],
      },
      {
        level: 4,
        descriptor:
          'Completes a twenty-four piece jigsaw, builds a structure with a purpose — an enclosure, a room, a bridge — and fills a five to seven piece outline with tangram pieces.',
        exemplars: [
          'Built a zoo with separate enclosures, and a hospital with rooms',
          'Fills a five-piece tangram cat, turning pieces to test them',
        ],
      },
      {
        level: 5,
        descriptor:
          'Says what a shape will look like after a turn or a flip before you move it, draws a simple top-down map of a familiar place, and builds from a picture of the finished thing.',
        exemplars: [
          'Predicted which way the L would point upside down, then checked by turning it',
          'Drew a top-down map of the house showing which room is next to which',
        ],
      },
    ],
    typicalMonths: [36, 48],
    prerequisites: [M('shapes')],
    assessmentHints: [
      'Leave a jigsaw one step above her usual out on the table and watch how she attacks it — edges first, or piece by piece?',
      'Play hide the toy using only position words, then swap and let her direct you. Giving the instruction is harder than following it.',
      'Hold up an asymmetric shape like an L and ask "what will this look like if I turn it upside down?" before you turn it.',
    ],
    sortOrder: 9,
  },
  {
    _id: M('addition'),
    domainId: mathDomain._id,
    domainKey: 'math',
    key: 'addition',
    name: 'Addition',
    description:
      'Joining two groups and finding how many altogether — first by pushing real objects together, then by counting on, then in her head.',
    levelDescriptors: [
      {
        level: 1,
        descriptor:
          'Notices that adding more makes more, without knowing the new total.',
        exemplars: [
          'Says "more!" when another grape lands on the plate',
          'Holds out a hand for one more block and looks pleased that the tower grew',
        ],
      },
      {
        level: 2,
        descriptor:
          'Finds the total of two small groups by pushing them together and counting the whole lot again from one, up to about four.',
        exemplars: [
          'Slides two blocks and one block together and counts "one, two, three"',
          'Puts both handfuls of raisins in a pile before counting them',
        ],
      },
      {
        level: 3,
        descriptor:
          'Adds two groups of real objects with totals up to five and answers "how many altogether?" after recounting.',
        exemplars: [
          'Addition with objects emerging at 2.75 years, totals to about five',
          'Two apples plus one apple makes three, counted out on the table',
        ],
      },
      {
        level: 4,
        descriptor:
          'Adds with totals to ten using objects, and counts on from the bigger group instead of starting over — "five… six, seven, eight".',
        exemplars: [
          'Keeps five in her head and counts the three new ones on her fingers',
          'Adds two dice in a board game by counting on from the larger one',
        ],
      },
      {
        level: 5,
        descriptor:
          'Solves a spoken word problem with nothing in front of her, and knows a few number pairs by heart.',
        exemplars: [
          '"Three friends came and then two more came" answered without counters',
          'Says two and two make four straight off, without counting',
        ],
      },
    ],
    typicalMonths: [40, 52],
    prerequisites: [M('correspondence')],
    assessmentHints: [
      'Snack time is the natural probe: "you have two, I\'ll give you one more — how many now?"',
      'Board game dice give you a free addition question every turn. Watch whether she recounts everything or counts on.',
      'Once she can do it with objects, ask the same question with your hands behind your back. Doing it without seeing is the next level.',
    ],
    sortOrder: 10,
  },
  {
    _id: M('subtraction'),
    domainId: mathDomain._id,
    domainKey: 'math',
    key: 'subtraction',
    name: 'Subtraction',
    description:
      'Taking some away and finding what is left, and the missing-part question — how many more do we need to get there?',
    levelDescriptors: [
      {
        level: 1,
        descriptor:
          'Notices when something has gone and says so, without tracking how many are left.',
        exemplars: [
          '"All gone!" as the plate empties',
          'Looks for the missing car after you slip one off the mat',
        ],
      },
      {
        level: 2,
        descriptor:
          'Counts what is left after you take one away from a small group, starting the count again from one.',
        exemplars: [
          'Counts the three remaining crackers after you eat one of four',
          'Recounts the blocks once one is knocked off the tower',
        ],
      },
      {
        level: 3,
        descriptor:
          'Takes away from a group of up to five with the objects in front of her and says how many are left.',
        exemplars: [
          'Take-away with objects introduced and understood at 2.75 years',
          'Eats two of five raspberries and says three are left',
        ],
      },
      {
        level: 4,
        descriptor:
          'Works with groups up to ten, and answers "how many more do we need?" by counting the gap between two groups.',
        exemplars: [
          'Counts four plates and six chairs and says two more plates are needed',
          'Counts down the last five minutes of tidy-up as objects disappear',
        ],
      },
      {
        level: 5,
        descriptor:
          'Solves a spoken take-away problem with nothing in front of her, and tells the story back both ways — what she had, what went, what is left.',
        exemplars: [
          '"We had five and we ate two, so three are left" said without counters',
          'Hides some of a known group under a cloth and works out how many are hidden',
        ],
      },
    ],
    typicalMonths: [42, 54],
    prerequisites: [M('addition')],
    assessmentHints: [
      'Eat one of her snacks with permission and ask how many are left. Real stakes make the question worth answering.',
      'Hide part of a group she has just counted under a cloth and ask how many are hiding. That is the missing-part question, which is harder than take-away.',
      'Count down as toys go into the box — "four left, three left" — and see whether she picks up the running total herself.',
    ],
    sortOrder: 11,
  },
  {
    _id: M('fractions'),
    domainId: mathDomain._id,
    domainKey: 'math',
    key: 'fractions',
    name: 'Halves & Fair Shares',
    description:
      'Splitting a whole into equal parts and naming them — halves first, then quarters — and judging whether the shares came out fair.',
    levelDescriptors: [
      {
        level: 1,
        descriptor:
          'Uses the word "half" loosely for any piece, and notices when a share looks unfair.',
        exemplars: [
          'Objects that her piece is smaller than yours',
          'Calls any broken-off bit "my half"',
        ],
      },
      {
        level: 2,
        descriptor:
          'Breaks a whole into two pieces and calls them halves even when they are plainly uneven.',
        exemplars: [
          'Snaps a cracker anywhere and says "now we both have half"',
          'Tears the paper in two and hands you the smaller part happily',
        ],
      },
      {
        level: 3,
        descriptor:
          'Cuts or folds a whole into two matching parts, and says two halves put back together make one whole.',
        exemplars: [
          'Folds paper in half, paints one side and presses it to make a mirror image',
          'Puts two half sandwiches back together and says it is one whole sandwich again',
        ],
      },
      {
        level: 4,
        descriptor:
          'Names quarters as well as halves, and shares a set of objects out fairly between two or three people by dealing one at a time.',
        exemplars: [
          'Cuts the pizza into four and gives one piece to each doll',
          'Deals grapes round the table one each, one each, until they run out',
        ],
      },
      {
        level: 5,
        descriptor:
          'Says whether a sharing is fair and proposes a fix, and knows that half of a small thing is smaller than half of a big thing.',
        exemplars: [
          'Notices five pieces and four friends and suggests cutting one piece in half',
          'Says her half of the small apple is less than your half of the big one',
        ],
      },
    ],
    typicalMonths: [48, 60],
    prerequisites: [M('comparison')],
    assessmentHints: [
      'Hand her the sandwich and ask her to make it fair for both of you, then watch where the cut lands.',
      'Give her one biscuit and three dolls and stay quiet. What she does next is the whole assessment.',
      'Fold and paint a symmetry print, then ask her to show you the two halves and put them back into one.',
    ],
    sortOrder: 12,
  },
]

// ─────────────────────────────────────────────────────────────
// Fine motor & pre-writing
// ─────────────────────────────────────────────────────────────

const F = (key: string): SkillId => sid('fineMotor', key)

export const fineMotorSkills: Skill[] = [
  {
    _id: F('threading'),
    domainId: fineMotorDomain._id,
    domainKey: 'fineMotor',
    key: 'threading',
    name: 'Threading & Lacing',
    description:
      'Feeding a lace through a hole while the other hand holds the target still. Two hands doing two different jobs at once.',
    levelDescriptors: [
      {
        level: 1,
        descriptor:
          'Pushes a large bead onto a stiff rod or a lace tip with hand-over-hand help, one at a time.',
        exemplars: [
          'Slides a chunky ring onto the post of a stacking toy',
          'Gets one bead on when you hold the lace steady for her',
        ],
      },
      {
        level: 2,
        descriptor:
          'Threads big beads onto a stiff lace on her own, pulling the lace through with her other hand.',
        exemplars: [
          'Threads four chunky beads without help and shakes the string proudly',
          'Pushes pasta tubes onto a pipe cleaner',
        ],
      },
      {
        level: 3,
        descriptor:
          'Threads small beads onto a floppy lace, holding the lace steady with one hand and feeding beads with the other.',
        exemplars: [
          'Threading mastered at 2.5 years',
          'Strings a whole bead necklace end to end in one sitting',
        ],
      },
      {
        level: 4,
        descriptor:
          'Threads to a plan — a colour pattern, or a set number of beads — and sews in and out through punched holes in card.',
        exemplars: [
          'Threads a red-blue-red-blue bracelet as a present for someone',
          'Sews around the edge of a punched card without skipping holes',
        ],
      },
      {
        level: 5,
        descriptor:
          'Laces a shoe or a lacing card with a real crossing pattern, and threads very small beads or a large-eye needle.',
        exemplars: [
          'Laces the card in a crossing X pattern rather than looping round the edge',
          'Gets thread through a large-eye plastic needle by herself',
        ],
      },
    ],
    typicalMonths: [30, 42],
    prerequisites: [],
    assessmentHints: [
      'Suggest a necklace for someone she loves — the caregiving frame keeps her at it far longer than a threading exercise would.',
      'Swap the stiff lace for a floppy one. Everything gets harder, and that is where the real skill shows.',
      'Watch the helper hand, not the threading hand. Whether it holds the lace steady is the thing that changes.',
    ],
    sortOrder: 1,
  },
  {
    _id: F('pencilGrip'),
    domainId: fineMotorDomain._id,
    domainKey: 'fineMotor',
    key: 'pencilGrip',
    name: 'Pencil Grip',
    description:
      'How the hand holds the tool and where the movement comes from — shoulder, elbow, wrist, then the fingers themselves.',
    levelDescriptors: [
      {
        level: 1,
        descriptor:
          'Holds a crayon in a fist with the point coming out of the bottom of the hand, and makes marks by swinging the whole arm from the shoulder.',
        exemplars: [
          'Palmar-supinate grasp — the arm makes the mark, not the hand',
          'Marks land anywhere on the page because the shoulder is doing the aiming',
        ],
      },
      {
        level: 2,
        descriptor:
          'Holds the crayon with the fingers on top and the thumb wrapped round, moving mostly from the elbow and wrist, and uses the same hand most of the time.',
        exemplars: [
          'Digital pronate grasp, hand hovering above the page',
          'The paper slides about because the other hand is not holding it down',
        ],
      },
      {
        level: 3,
        descriptor:
          'Holds the pencil between the thumb and the first two fingers with the other hand steadying the paper, moving mostly from the wrist.',
        exemplars: [
          'Four-finger or early tripod grip with the hand resting on the page',
          'Holds the page down with her spare hand without being told to',
        ],
      },
      {
        level: 4,
        descriptor:
          'Uses a mature tripod grip — thumb, index and middle finger, with the ring and little finger tucked in — and the fingers make the small movements while the wrist rests on the page.',
        exemplars: [
          'Mature tripod grip at 2.5 years, roughly eighteen months ahead of the usual timing',
          'Draws small details without moving her whole hand across the page',
        ],
      },
      {
        level: 5,
        descriptor:
          'Keeps the tripod grip across pencil, crayon, marker, brush and chalk, changes pressure on purpose for light and dark lines, and works several minutes without the hand tiring.',
        exemplars: [
          'Presses hard for a bold oil pastel outline, then lightly for shading',
          'Switches from a fat chalk to a fine marker without the grip collapsing',
        ],
      },
    ],
    typicalMonths: [48, 60],
    prerequisites: [],
    assessmentHints: [
      'Just watch during free drawing. A grip you asked her to use is not the grip she owns.',
      'Offer broken crayons or short golf pencils. A stub is too small to fist, so it invites the fingers into position without a word from you.',
      'Look at the hand that is not drawing. Steadying the paper is half of what a mature grip is for.',
    ],
    sortOrder: 2,
  },
  {
    _id: F('tracing'),
    domainId: fineMotorDomain._id,
    domainKey: 'fineMotor',
    key: 'tracing',
    name: 'Tracing & Copying Lines',
    description:
      'Making the pencil follow a path someone else drew, and copying a shape from a model. The eye leads and the hand follows.',
    levelDescriptors: [
      {
        level: 1,
        descriptor:
          'Scribbles over or near a printed line rather than along it.',
        exemplars: [
          'Covers the dotted line with a cloud of marks',
          'Enjoys the movement and pays no attention to the path',
        ],
      },
      {
        level: 2,
        descriptor:
          'Traces a straight line and a big circle roughly along the path, with the line wandering off and coming back.',
        exemplars: [
          'Follows a thick straight line most of the way across the page',
          'Traces a large circle with a wobbly, mostly closed loop',
        ],
      },
      {
        level: 3,
        descriptor:
          'Traces straight lines, circles and simple shapes staying on the line most of the way, and copies a vertical line, a horizontal line and a circle after watching you draw one.',
        exemplars: [
          'Tracing lines and shapes capable at 2.5 years',
          'Copies a cross after watching you make the two strokes',
        ],
      },
      {
        level: 4,
        descriptor:
          'Traces curves, zigzags and letter shapes, copies a square and a triangle from a model with real corners, and colours inside a boundary most of the time.',
        exemplars: [
          'Copies a square with four corners rather than a rounded blob',
          'Traces a zigzag without rounding the points off',
        ],
      },
      {
        level: 5,
        descriptor:
          'Traces or copies a whole word or a detailed outline, keeps a steady line inside a narrow track such as a maze, and copies a diagonal shape like a diamond.',
        exemplars: [
          'Runs a pencil through a narrow maze without touching the walls',
          'Copies a diamond, which needs two diagonals meeting at a point',
        ],
      },
    ],
    typicalMonths: [36, 48],
    prerequisites: [F('pencilGrip')],
    assessmentHints: [
      'Draw a shape and say "your turn" without explaining how. Copying from a model shows more than tracing over dots.',
      'Mazes and dot-to-dots put line control inside a game, so you get several minutes of evidence instead of one attempt.',
      'Trace round her own hand, then let her trace round yours. The uneven outline is a harder path than any printed one.',
    ],
    sortOrder: 3,
  },
  {
    _id: F('scissors'),
    domainId: fineMotorDomain._id,
    domainKey: 'fineMotor',
    key: 'scissors',
    name: 'Scissors',
    description:
      'Opening and closing the scissors while the other hand feeds and turns the paper. Two hands, two different jobs, both moving.',
    levelDescriptors: [
      {
        level: 1,
        descriptor:
          'Opens and closes the scissors and snips the edge of the paper without cutting across it.',
        exemplars: [
          'Makes a fringe of snips along one edge',
          'Needs the paper held for her while she squeezes',
        ],
      },
      {
        level: 2,
        descriptor:
          'Makes a run of snips and cuts right across a narrow strip of paper, holding the paper herself with the other hand.',
        exemplars: [
          'Cuts a paper strip into little pieces for a collage',
          'Cuts a playdough snake into slices',
        ],
      },
      {
        level: 3,
        descriptor:
          'Cuts along a straight drawn line across a whole sheet staying near the line, and turns the paper with her free hand instead of turning her body.',
        exemplars: [
          'Cuts along drawn lines capably at 2.5 years',
          'Rotates the sheet as she cuts so the scissors stay pointed away from her',
        ],
      },
      {
        level: 4,
        descriptor:
          'Cuts out a simple shape by following a curved line, and handles thicker card without stalling.',
        exemplars: [
          'Cut curved vehicle shapes out for a collage',
          'Cuts a circle out staying close to the drawn edge all the way round',
        ],
      },
      {
        level: 5,
        descriptor:
          'Cuts out a detailed shape with both corners and curves, and cuts along a line she drew herself for a purpose — the cover of her book, a mask, a paper doll.',
        exemplars: [
          'Cuts out her own drawn cat and glues it into a scene',
          'Cuts a star with points that survive the cutting',
        ],
      },
    ],
    typicalMonths: [42, 54],
    prerequisites: [],
    assessmentHints: [
      'Give her a strip of paper and a job while you cook — the cutting happens without being an activity.',
      'Watch the helper hand. Turning the paper rather than twisting her wrist is the real progress marker.',
      'Draw a wobbly line and ask her to follow it. A curve tells you far more than a straight edge.',
    ],
    sortOrder: 4,
  },
  {
    _id: F('drawingRepresentation'),
    domainId: fineMotorDomain._id,
    domainKey: 'fineMotor',
    key: 'drawingRepresentation',
    name: 'Representational Drawing',
    description:
      'Drawing that stands for something real. Lowenfeld\'s ladder runs from marks made for the joy of moving, through the first stored symbols for a person and a house, to drawing what is actually in front of her.',
    levelDescriptors: [
      {
        level: 1,
        descriptor:
          'Makes marks for the pleasure of the movement and names them afterwards — "that\'s a doggy" about a scribble already on the page.',
        exemplars: [
          'Lowenfeld: disordered scribbling, then naming what appeared',
          'The name changes if you ask twice',
        ],
      },
      {
        level: 2,
        descriptor:
          'Draws closed circles and deliberate lines, and says what she is going to draw before she starts.',
        exemplars: [
          'Lowenfeld: controlled scribbling with naming and intention',
          '"I\'m drawing mama" said before the pencil touches the paper',
        ],
      },
      {
        level: 3,
        descriptor:
          'Draws a recognizable person as a head with legs coming straight out of it, with eyes and a mouth, and a house as a square with a triangle roof.',
        exemplars: [
          'Lowenfeld: entry into the preschematic stage, typically around age four',
          'The tadpole person: a big head, two legs, two dots and a line',
        ],
      },
      {
        level: 4,
        descriptor:
          'Draws a repeated stored symbol — her cat, her house — with a body, limbs and small details like fingers, hair and clothing, and puts more than one thing on the page.',
        exemplars: [
          'Detailed cats, houses and bodies with features at 2.75 years — preschematic, a year or more early',
          '"Draws on everything" — the doorway, the floor, every loose sheet in the house',
        ],
      },
      {
        level: 5,
        descriptor:
          'Draws what she is looking at rather than her stored symbol, arranges a scene on a ground line with sizes that relate to each other, and attempts a side-view profile face.',
        exemplars: [
          'Air-traces the forehead, nose, lips and chin silhouette with a finger, then draws that outline',
          'Draws the cat on the mat next to the house, all sitting on a ground line',
        ],
      },
    ],
    typicalMonths: [48, 60],
    prerequisites: [F('pencilGrip')],
    assessmentHints: [
      'Leave paper and pencils where she can reach them and look at what she makes unasked. Free drawing is the honest sample.',
      'Ask her to tell you about the drawing rather than guessing what it is. Her words tell you what she intended.',
      'Put a real object in front of her and ask what she can see, then let her draw. Whether she draws the object or her stored symbol is the whole question.',
      'Keep one self-portrait every few months. Two of them side by side say more than any single drawing.',
    ],
    sortOrder: 5,
  },
  {
    _id: F('letterFormation'),
    domainId: fineMotorDomain._id,
    domainKey: 'fineMotor',
    key: 'letterFormation',
    name: 'Letter Formation',
    description:
      'How a letter gets built — where the stroke starts, which way it travels, in what order. Correct habits now cost nothing; undoing them later costs a lot.',
    levelDescriptors: [
      {
        level: 1,
        descriptor:
          'Makes letter-like marks with no fixed starting point, drawn from wherever the pencil happens to land.',
        exemplars: [
          'Mock letters scattered through a drawing',
          'A row of shapes she reads back as writing',
        ],
      },
      {
        level: 2,
        descriptor:
          'Copies a simple letter shape from a model, building it out of separate strokes in any order — a line drawn upward, a cross drawn right to left.',
        exemplars: [
          'Makes a T by drawing the crossbar first, then the stem upward',
          'Gets the shape right by a route no adult would take',
        ],
      },
      {
        level: 3,
        descriptor:
          'Starts most letters at the top and pulls down, and forms the straight-line letters (L, T, I, H, E, F) in a settled stroke order.',
        exemplars: [
          'Formed L, T, I and H from the top down in a multisensory chalk session',
          'Says "start at the top" to herself before the pencil lands',
        ],
      },
      {
        level: 4,
        descriptor:
          'Forms the curved and diagonal letters (C, O, S, G, A, V, W, M) with a settled stroke order, and keeps neighbouring letters roughly the same size.',
        exemplars: [
          'Wrote C, O, S and G in chalk with the curves starting at the top',
          'The letters in her name come out about the same height as each other',
        ],
      },
      {
        level: 5,
        descriptor:
          'Forms uppercase and lowercase with correct stroke order unprompted, sits the letters on a baseline, and leaves spaces between words.',
        exemplars: [
          'Writes a caption along the bottom of a drawing with the letters sitting on the line',
          'Leaves a finger-width gap between two written words',
        ],
      },
    ],
    typicalMonths: [48, 60],
    prerequisites: [F('tracing')],
    assessmentHints: [
      'Watch where the pencil lands first and which way it moves. The finished letter can look right and still be built upside down.',
      'Form letters in sand, in shaving cream, with playdough snakes, or big on a chalkboard. The big-muscle version shows the stroke path clearly and is more fun than paper.',
      'Say nothing about neatness. At this stage the stroke path is the skill, and comments on tidiness cost willingness.',
    ],
    sortOrder: 6,
  },
  {
    _id: F('uppercaseWriting'),
    domainId: fineMotorDomain._id,
    domainKey: 'fineMotor',
    key: 'uppercaseWriting',
    name: 'Uppercase Writing',
    description:
      'Producing capital letters. Capitals come first for most children — they are all straight lines and simple curves, and they sit in one band with no tails.',
    levelDescriptors: [
      {
        level: 1,
        descriptor:
          'Makes marks she calls letters, and a real letter appears now and then by accident.',
        exemplars: [
          'A recognisable O turns up inside a scribble',
          'Points at her marks and reads them back as her name',
        ],
      },
      {
        level: 2,
        descriptor:
          'Copies two or three capitals from a model, most often the first letter of her own name.',
        exemplars: [
          'Copies A from a model, then makes several more A shapes',
          'Writes a wobbly M after watching you write it',
        ],
      },
      {
        level: 3,
        descriptor:
          'Copies most capital letters from a model, and writes the first letter of her name with nothing to copy.',
        exemplars: [
          'Wrote A, H and a run of "P A B C" in chalk',
          'Signs a drawing with a single big A',
        ],
      },
      {
        level: 4,
        descriptor:
          'Writes her whole name in capitals with a model in front of her and writes many capitals on request. Some letters come out reversed, which is expected at this stage.',
        exemplars: [
          'Writes AURORA with a model — "A U R O R A", uppercase only, and loves doing it',
          'Writes the letters of a word she is learning to read, one at a time',
        ],
      },
      {
        level: 5,
        descriptor:
          'Writes her name and short words in capitals from memory, and uses capitals for a real purpose — a sign, a label, a card.',
        exemplars: [
          'Makes a sign for her bedroom door',
          'Labels the cat in her drawing with CAT and no model in sight',
        ],
      },
    ],
    typicalMonths: [42, 54],
    prerequisites: [F('letterFormation')],
    assessmentHints: [
      'Chalk on the ground and a whiteboard get far more writing out of her than a pencil and a worksheet.',
      'Ask her to sign or label a drawing she already cares about. Writing with a purpose gets attempted; writing as an exercise gets skipped.',
      'Quietly slide the model out of sight halfway through and see whether she keeps going.',
    ],
    sortOrder: 7,
  },
  {
    _id: F('lowercaseWriting'),
    domainId: fineMotorDomain._id,
    domainKey: 'fineMotor',
    key: 'lowercaseWriting',
    name: 'Lowercase Writing',
    description:
      'Producing lowercase letters, which is worth building because almost everything she reads is lowercase. Best taught by formation family rather than by alphabet order.',
    levelDescriptors: [
      {
        level: 1,
        descriptor:
          'Reads lowercase easily but writes only capitals, and turns a lowercase model into a capital when copying.',
        exemplars: [
          'Reads lowercase text fluently and writes AURORA in capitals',
          'Copies "cat" and produces CAT',
        ],
      },
      {
        level: 2,
        descriptor:
          'Copies the lowercase letters that are simply smaller versions of their capitals — c, o, s, v, w, x, z — from a model.',
        exemplars: [
          'Copies c and o easily because there is nothing new to learn but size',
          'Copies v and w by counting the points',
        ],
      },
      {
        level: 3,
        descriptor:
          'Writes the c, o, s, v, w, x, z group with no model, and copies the circle-and-line family — a, d, g, q — which all start the same way as c.',
        exemplars: [
          'Writes c, o and s on the whiteboard from memory',
          'Makes an a by drawing a c and closing it with a line',
        ],
      },
      {
        level: 4,
        descriptor:
          'Writes the tall and tailed letters — b, h, p, y — with the tall parts tall and the tails dropping below the line, and writes most of the lowercase alphabet from a model.',
        exemplars: [
          'Writes "dog" with the g tail hanging below the other letters',
          'Keeps the h taller than the o next to it',
        ],
      },
      {
        level: 5,
        descriptor:
          'Writes whole words in lowercase from memory with the letters sized against each other, and starts a name or a sentence with a capital and continues in lowercase.',
        exemplars: [
          'Writes "Cat" with one capital and two lowercase letters',
          'Writes a caption in lowercase because that is how her books look',
        ],
      },
    ],
    typicalMonths: [54, 66],
    prerequisites: [F('letterFormation'), F('uppercaseWriting')],
    assessmentHints: [
      'Offer the c, o, s, v, w, x, z group first. They are just small capitals, so the win comes immediately and the appetite follows.',
      'Put lowercase inside a label on her own drawing rather than on a practice sheet. Isolated letter practice is the thing that gets skipped.',
      'Look at an ascender and a descender side by side — an h next to an o, a g next to an a. Relative height is what makes lowercase readable.',
    ],
    sortOrder: 8,
  },
  {
    _id: F('freehandWriting'),
    domainId: fineMotorDomain._id,
    domainKey: 'fineMotor',
    key: 'freehandWriting',
    name: 'Freehand Writing',
    description:
      'Writing with nothing to copy from — pulling the letter shapes out of memory instead of off a model.',
    levelDescriptors: [
      {
        level: 1,
        descriptor:
          'Writes only with a model right beside her, and stops when the model is taken away.',
        exemplars: [
          'Looks up at the card between every single letter',
          'Asks "how do you make it?" when the card is gone',
        ],
      },
      {
        level: 2,
        descriptor:
          'Writes one or two letters from memory, usually the first letter of her own name.',
        exemplars: [
          'Puts an A on everything she makes',
          'Writes M for mama with nothing to copy',
        ],
      },
      {
        level: 3,
        descriptor:
          'Writes her whole name from memory with no model, letters possibly uneven or reversed.',
        exemplars: [
          'Freehand writing beginning at 2.75 years',
          'Signs her drawing without asking how the letters go',
        ],
      },
      {
        level: 4,
        descriptor:
          'Writes several known words from memory — her name, MOM, CAT — and fills a word into a label or a card without asking for a model.',
        exemplars: [
          'Writes CAT under her cat drawing unprompted',
          'Writes a family member\'s name on a card she made for them',
        ],
      },
      {
        level: 5,
        descriptor:
          'Writes a short sentence from memory with spaces between the words and nothing to copy from.',
        exemplars: [
          'Writes "I LIK MY CAT" as the caption on a page of her own book',
          'Writes a shopping list of three words while you cook',
        ],
      },
    ],
    typicalMonths: [48, 60],
    prerequisites: [F('uppercaseWriting')],
    assessmentHints: [
      'Cover the model halfway through and see whether the writing carries on or stops.',
      'Ask her to write a word she can already read. Reading it and writing it are two directions of the same skill, and the writing direction lags.',
      'Give her a real reason — a label, a card, a sign, a list. She writes freehand for a purpose long before she does it on request.',
    ],
    sortOrder: 9,
  },
  {
    _id: F('inventedSpelling'),
    domainId: fineMotorDomain._id,
    domainKey: 'fineMotor',
    key: 'inventedSpelling',
    name: 'Invented Spelling',
    description:
      'Writing a word the way it sounds — KAT, HOWS, LIK. Gentry\'s stages run from letter strings with no sound in them to a letter for every sound she can hear. Never corrected: the sound analysis is the skill, and correct spelling arrives later on its own.',
    levelDescriptors: [
      {
        level: 1,
        descriptor:
          'Writes strings of letters and letter-like marks that carry a message to her but have no sounds mapped into them.',
        exemplars: [
          'Gentry: precommunicative stage',
          'Writes a long row of letters and reads it back as a whole sentence',
        ],
      },
      {
        level: 2,
        descriptor:
          'Writes one letter, almost always the first sound, to stand for a whole word.',
        exemplars: [
          'Gentry: early semiphonetic — writes K for cat',
          'Labels a drawing of a dog with a single D',
        ],
      },
      {
        level: 3,
        descriptor:
          'Writes the first and last sounds of a word — KT for cat, DG for dog — and can read back what it says.',
        exemplars: [
          'Gentry: semiphonetic — the ends of the word are heard, the middle is not yet',
          'Writes MM for mum and points at it while reading it aloud',
        ],
      },
      {
        level: 4,
        descriptor:
          'Writes a letter for most of the sounds she can hear, vowels included — KAT, HOWS, LIK — and writes several such words in one sitting.',
        exemplars: [
          'Gentry: phonetic — KAT captures all three sounds of cat',
          'Writes HOWS next to her house drawing without asking for help',
        ],
      },
      {
        level: 5,
        descriptor:
          'Spells many common words conventionally, puts a vowel in every syllable, and reaches for long unfamiliar words by stretching them out rather than asking.',
        exemplars: [
          'Gentry: transitional — writes "like" after meeting it often in books, having written LIK before',
          'Attempts DINOSOR on her own instead of asking how it is spelled',
        ],
      },
    ],
    typicalMonths: [54, 66],
    prerequisites: [F('freehandWriting')],
    assessmentHints: [
      'Never correct the spelling. Say what she got: "you heard the /k/, the /a/ AND the /t/". Praising the sound analysis is what grows it.',
      'When she is stuck, stretch the word aloud — "ccc-aaa-t" — and ask what she hears first, then what comes next.',
      'Count how many sounds of the word made it onto the page. That count moving from one to two to three is the progress, not the spelling being right.',
      'Give her a reason to write words nobody has spelled for her: a menu, a shopping list, a caption in her own book.',
    ],
    sortOrder: 10,
  },
]

// ─────────────────────────────────────────────────────────────
// Gross motor & physical
// ─────────────────────────────────────────────────────────────

const G = (key: string): SkillId => sid('grossMotor', key)

export const grossMotorSkills: Skill[] = [
  {
    _id: G('climbing'),
    domainId: grossMotorDomain._id,
    domainKey: 'grossMotor',
    key: 'climbing',
    name: 'Climbing',
    description:
      'Getting up and down a structure under her own power, judging where hands and feet can go.',
    levelDescriptors: [
      {
        level: 1,
        descriptor:
          'Climbs onto a low sofa or a step using hands and knees together.',
        exemplars: [
          'Gets onto the sofa by pulling with both arms and kicking a knee up',
          'Crawls up a shallow slide on all fours',
        ],
      },
      {
        level: 2,
        descriptor:
          'Climbs a small playground ladder with your hand nearby, leading with the same foot on every rung.',
        exemplars: [
          'Goes up three rungs always leading with the right foot',
          'Climbs up happily and wants help coming down',
        ],
      },
      {
        level: 3,
        descriptor:
          'Climbs playground structures on her own — ladders, low nets, steps — and comes back down under control rather than dropping.',
        exemplars: [
          'Climbs playground structures independently at 2.5 years',
          'Turns around to come down a ladder backwards without being told',
        ],
      },
      {
        level: 4,
        descriptor:
          'Alternates feet going up and down stairs without holding the rail, and climbs taller structures, low walls and low branches while choosing her handholds.',
        exemplars: [
          'One foot per stair going down, hands free',
          'Tests a branch with a hand before putting weight on it',
        ],
      },
      {
        level: 5,
        descriptor:
          'Looks at an unfamiliar structure, plans a route before starting, and climbs it with changes of direction and reaches across gaps.',
        exemplars: [
          'Says where she is going to put her hands before starting the climb',
          'Traverses sideways across a climbing frame instead of only going up',
        ],
      },
    ],
    typicalMonths: [30, 36],
    prerequisites: [],
    assessmentHints: [
      'The playground does the assessing. Stand back and watch which structures she chooses without help.',
      'Watch the feet on stairs. One foot per step, both up and down, is the marker worth noticing.',
      'Ask "where are you going to put your hands next?" — planning the climb out loud is a level above doing it.',
    ],
    sortOrder: 1,
  },
  {
    _id: G('jumping'),
    domainId: grossMotorDomain._id,
    domainKey: 'grossMotor',
    key: 'jumping',
    name: 'Jumping',
    description:
      'Getting both feet off the ground together and landing on both feet — the base that hopping, gymnastics and jump rope all stand on.',
    levelDescriptors: [
      {
        level: 1,
        descriptor:
          'Steps down off a low step one foot at a time and bounces on the spot, with both feet only briefly leaving the ground.',
        exemplars: [
          'Bends and springs on the spot with the toes barely clearing the floor',
          'Steps down from the bottom stair rather than jumping off it',
        ],
      },
      {
        level: 2,
        descriptor:
          'Jumps in place with both feet clearly leaving the ground at the same time.',
        exemplars: [
          'Jumps on the spot in a song and lands on two feet',
          'Bounces on a trampoline getting real air',
        ],
      },
      {
        level: 3,
        descriptor:
          'Jumps forward with two feet together and lands on two feet, and jumps down from a low step.',
        exemplars: [
          'Two-foot jumping mastered at 2.5 years',
          'Jumps off the bottom stair and lands on both feet',
        ],
      },
      {
        level: 4,
        descriptor:
          'Jumps over a line or a low object, jumps forward several times in a row, and swings her arms to go further.',
        exemplars: [
          'Clears a rope lying on the ground without stopping first',
          'Makes four forward jumps in a row down the hall',
        ],
      },
      {
        level: 5,
        descriptor:
          'Jumps for distance or height on purpose, lands with bent knees to absorb it, and links jumps into a sequence — jump, turn, jump.',
        exemplars: [
          'Marks a spot and tries to beat her own jump',
          'Jumps through a chalk hopscotch grid two feet at a time',
        ],
      },
    ],
    typicalMonths: [24, 30],
    prerequisites: [],
    assessmentHints: [
      'Chalk two lines on the ground as a "river" and see how wide she can make it before she steps instead of jumping.',
      'Watch the landing, not the take-off. Two feet together and knees bending is what tells you it is a real jump.',
      'Watch the arms. Swinging them forward is the step that turns a bounce into a distance jump.',
    ],
    sortOrder: 2,
  },
  {
    _id: G('balance'),
    domainId: grossMotorDomain._id,
    domainKey: 'grossMotor',
    key: 'balance',
    name: 'Balance',
    description:
      'Holding the body steady on a small base — one foot, a narrow line, a beam — and recovering when it wobbles.',
    levelDescriptors: [
      {
        level: 1,
        descriptor:
          'Stands still on two feet without wobbling and walks along a line on the floor with arms out for stability.',
        exemplars: [
          'Walks a tape line on the floor with both arms held wide',
          'Stands still on the spot while getting dressed, holding on',
        ],
      },
      {
        level: 2,
        descriptor:
          'Stands on one foot for a moment while holding your hand, and walks a few heel-to-toe steps.',
        exemplars: [
          'Lifts one foot for a second or two with a hand on the wall',
          'Takes three heel-to-toe steps along a line before stepping off',
        ],
      },
      {
        level: 3,
        descriptor:
          'Stands on one foot for three seconds or more with nothing to hold, and walks forward along a beam or a curb.',
        exemplars: [
          'One-foot balance at 2.5 years',
          'Walks the length of a low curb with arms out',
        ],
      },
      {
        level: 4,
        descriptor:
          'Stands on one foot for eight seconds or more, walks a beam sideways and backwards, and holds a steady pose while carrying something.',
        exemplars: [
          'Walked the beam carrying a tray in the waiter game',
          'Balances on one foot long enough to pull a sock on',
        ],
      },
      {
        level: 5,
        descriptor:
          'Balances on one foot with her eyes closed or on an unstable surface, and recovers from a wobble without stepping off.',
        exemplars: [
          'Holds a one-foot pose on a cushion and wobbles back to centre',
          'Stands on one leg with eyes shut and counts to three',
        ],
      },
    ],
    typicalMonths: [30, 42],
    prerequisites: [],
    assessmentHints: [
      'Every curb on a walk is a balance beam. Offer a hand and see whether she takes it.',
      'Play freeze games and call a one-foot freeze. Counting out loud together turns a test into a game.',
      'Try one-foot balance while brushing teeth. Doing it while attention is elsewhere shows how automatic it has become.',
    ],
    sortOrder: 3,
  },
  {
    _id: G('hopping'),
    domainId: grossMotorDomain._id,
    domainKey: 'grossMotor',
    key: 'hopping',
    name: 'Hopping',
    description:
      'Leaving the ground from one foot and landing on the same foot. It needs the jump and the one-foot balance together, which is why it arrives later than both.',
    levelDescriptors: [
      {
        level: 1,
        descriptor:
          'Lifts one foot and bounces on the other with a hand held, without leaving the ground.',
        exemplars: [
          'Bobs up and down on one leg holding your hand, toes staying down',
          'Lifts a foot for a "flamingo" and puts it straight back',
        ],
      },
      {
        level: 2,
        descriptor:
          'Hops once or twice on her preferred foot with support or a wall to touch.',
        exemplars: [
          'Gets one clean hop with a fingertip on the wall',
          'Hops once, lands on two feet, and laughs',
        ],
      },
      {
        level: 3,
        descriptor:
          'Hops two or three times in a row on one foot with nothing to hold.',
        exemplars: [
          'Three hops on the spot on the right foot before putting the other down',
          'Hops to the door on one foot and arrives on two',
        ],
      },
      {
        level: 4,
        descriptor:
          'Hops five or more times in a row on her preferred foot and travels forward while hopping.',
        exemplars: [
          'Hops the length of the hallway on one foot',
          'Uses her arms to keep the rhythm going across several hops',
        ],
      },
      {
        level: 5,
        descriptor:
          'Hops on either foot, hops through a hopscotch grid changing feet, and gallops or skips with alternating feet.',
        exemplars: [
          'Skips with alternating feet down the pavement',
          'Runs a hopscotch grid with the one-foot and two-foot squares in the right places',
        ],
      },
    ],
    typicalMonths: [36, 48],
    prerequisites: [G('jumping'), G('balance')],
    assessmentHints: [
      'Chalk a hopscotch grid. It puts the counting, the balance and the hopping in one game.',
      'Play animals — bunny hops on two feet, flamingo hops on one — so the one-foot version has a reason to exist.',
      'Count hops out loud together. The count gives her a rhythm, and rhythm is most of what keeps a hop chain going.',
    ],
    sortOrder: 4,
  },
  {
    _id: G('riding'),
    domainId: grossMotorDomain._id,
    domainKey: 'grossMotor',
    key: 'riding',
    name: 'Riding',
    description:
      'Balance bike, tricycle, scooter and bike. Steering and propelling while the body stays upright on a moving base.',
    levelDescriptors: [
      {
        level: 1,
        descriptor:
          'Sits astride a ride-on toy and pushes it along with both feet flat on the ground.',
        exemplars: [
          'Walks a ride-on car along the hall while sitting on it',
          'Sits on the balance bike and shuffles forward',
        ],
      },
      {
        level: 2,
        descriptor:
          'Scoots a balance bike along with both feet and keeps it upright, and turns a tricycle\'s pedals a few times with help.',
        exemplars: [
          'Pushes off with alternating feet and rolls a short way',
          'Gets a few pedal turns once you place her feet',
        ],
      },
      {
        level: 3,
        descriptor:
          'Pedals a tricycle steadily and steers it round obstacles, or glides a balance bike with both feet up for a few seconds.',
        exemplars: [
          'Can ride a tricycle and a balance bike at 2.5 years',
          'Steers the trike around a cone without stopping to reposition',
        ],
      },
      {
        level: 4,
        descriptor:
          'Glides a balance bike with feet up for long stretches, leans into turns, and starts and stops where she means to.',
        exemplars: [
          'Glides the whole length of the path with feet up',
          'Stops on a chalk mark on the driveway',
        ],
      },
      {
        level: 5,
        descriptor:
          'Rides a pedal bike without training wheels, or handles a scooter with steering, braking and a change of surface.',
        exemplars: [
          'Pushes off, gets both feet on the pedals and rides away',
          'Rides from smooth path onto grass without losing balance',
        ],
      },
    ],
    typicalMonths: [30, 42],
    prerequisites: [G('balance')],
    assessmentHints: [
      'Time how long the feet stay off the ground on a balance bike. That number is the readiness signal for pedals.',
      'Set out two cones or two chalk marks and ask her to ride between them, then to stop on one.',
      'Ride somewhere with a gentle slope. Gliding downhill gets the feet up naturally, without anyone asking for it.',
    ],
    sortOrder: 5,
  },
  {
    _id: G('kicking'),
    domainId: grossMotorDomain._id,
    domainKey: 'grossMotor',
    key: 'kicking',
    name: 'Kicking',
    description:
      'Striking a ball with the foot — standing on one leg for a moment while the other swings.',
    levelDescriptors: [
      {
        level: 1,
        descriptor:
          'Walks into a still ball and pushes it along with a foot, with no backswing.',
        exemplars: [
          'Bumps the ball forward a few inches by walking into it',
          'Nudges the ball with the toe and follows it',
        ],
      },
      {
        level: 2,
        descriptor:
          'Kicks a still ball forward with a small backswing, sometimes missing it entirely.',
        exemplars: [
          'Swings the leg back a little and connects about half the time',
          'Kicks and puts the foot down straight away to keep her balance',
        ],
      },
      {
        level: 3,
        descriptor:
          'Kicks a still ball forward with a proper leg swing and kicks it back and forth with another person.',
        exemplars: [
          'Ball kicking mastered at 2.5 years — kicks back and forth',
          'Sends the ball far enough that you have to move to get it',
        ],
      },
      {
        level: 4,
        descriptor:
          'Kicks a slowly rolling ball, aims for a target or a goal, and plants the other foot beside the ball as she kicks.',
        exemplars: [
          'Scores between two chairs set up as a goal',
          'Meets a ball you roll gently to her instead of waiting for it to stop',
        ],
      },
      {
        level: 5,
        descriptor:
          'Dribbles a ball with small touches while walking or running, and passes to someone who is moving.',
        exemplars: [
          'Takes the ball down the garden with several small touches',
          'Passes ahead of you rather than at where you were standing',
        ],
      },
    ],
    typicalMonths: [30, 36],
    prerequisites: [],
    assessmentHints: [
      'Set up two chairs as a goal and take turns. Aim only shows up when there is somewhere to aim.',
      'Roll the ball to her slowly instead of placing it. Kicking a moving ball is a whole level above kicking a still one.',
      'Watch the standing foot. Planting it beside the ball is the change that makes the kick strong.',
    ],
    sortOrder: 6,
  },
  {
    _id: G('throwing'),
    domainId: grossMotorDomain._id,
    domainKey: 'grossMotor',
    key: 'throwing',
    name: 'Throwing',
    description:
      'Sending a ball where she means it to go. Progress runs from arm-only to whole-body — step, turn, rotate.',
    levelDescriptors: [
      {
        level: 1,
        descriptor:
          'Drops or flings a ball with a stiff arm, mostly downward, with her body square to the front.',
        exemplars: [
          'Lets go too late and the ball lands at her feet',
          'Throws with both hands from the chest',
        ],
      },
      {
        level: 2,
        descriptor:
          'Throws overhand with the arm alone, a few feet, with no step and no body turn.',
        exemplars: [
          'Gets the ball a couple of metres with an arm-only throw',
          'Feet stay planted through the whole throw',
        ],
      },
      {
        level: 3,
        descriptor:
          'Throws a ball overhand several feet forward and underhand toward a person, aiming in the right general direction.',
        exemplars: [
          'Ball throwing good at 2.5 years',
          'Throws a beanbag toward you and it arrives near enough to reach',
        ],
      },
      {
        level: 4,
        descriptor:
          'Steps forward with the opposite foot as she throws, and hits a big target from a few feet.',
        exemplars: [
          'Lands beanbags in a laundry basket from three feet, most tries',
          'Steps into the throw without being reminded',
        ],
      },
      {
        level: 5,
        descriptor:
          'Turns her side to the target and rotates her trunk into the throw, throws for distance or accuracy on request, and throws a soft underhand ball gently enough for someone else to catch it.',
        exemplars: [
          'Throws a gentle underhand lob so you can catch it easily',
          'Chooses a hard throw for distance and a soft throw for a person',
        ],
      },
    ],
    typicalMonths: [36, 48],
    prerequisites: [],
    assessmentHints: [
      'Rolled socks and a laundry basket turn throwing practice into a game you can run indoors.',
      'Watch the feet. A step forward with the opposite foot is the single biggest change in a throw.',
      'Ask her to throw so YOU can catch it. Throwing gently and accurately for another person is its own skill, and it is the one that makes catching practice possible.',
    ],
    sortOrder: 7,
  },
  {
    _id: G('catching'),
    domainId: grossMotorDomain._id,
    domainKey: 'grossMotor',
    key: 'catching',
    name: 'Catching',
    description:
      'Receiving a moving object with the hands. This is a timing skill, not a strength or understanding skill: the hands have to close at the moment of contact, and that reflex is built at very close range with a slow, soft object before any distance is added.',
    levelDescriptors: [
      {
        level: 1,
        descriptor:
          'Holds straight arms out like a basket and traps the ball against her body. The arms move before the ball arrives or after it has already bounced off.',
        exemplars: [
          'The hands do not close in time and the ball comes off the chest or arms',
          'The arms clamp shut a beat after the ball has passed',
        ],
      },
      {
        level: 2,
        descriptor:
          'Closes her hands on a large soft ball or a balloon placed into them from about two feet, and keeps hold of it several times in a row.',
        exemplars: [
          'Ten easy successes at arm\'s length before any distance is added',
          'The hands close as the ball touches them, not before and not after',
        ],
      },
      {
        level: 3,
        descriptor:
          'Catches a large soft ball thrown gently underhand from about four feet, using her hands and her chest together, most of the time.',
        exemplars: [
          'Holds a soft ball five times out of ten at four feet',
          'Puts her hands up before the ball leaves your hand',
        ],
      },
      {
        level: 4,
        descriptor:
          'Catches a medium ball from about six feet with her hands alone, and catches a ball that bounces once on the way to her.',
        exemplars: [
          'Catches without pulling the ball into her chest',
          'Takes a bounce pass across the room',
        ],
      },
      {
        level: 5,
        descriptor:
          'Catches a small ball such as a tennis ball from several feet, catches one thrown a little to the side, and catches while moving.',
        exemplars: [
          'Steps sideways to meet a ball thrown wide and holds it',
          'Catches a tennis ball from five feet',
        ],
      },
    ],
    typicalMonths: [42, 54],
    prerequisites: [],
    assessmentHints: [
      'Sit on the floor about two feet apart and place a soft ball into her hands underhand. Watch her hands, not the ball: do they close as it touches, early, or late?',
      'Use a balloon. It falls slowly, which gives the hands time, and it separates the timing from everything else going on.',
      'Ask her to hold her hands up as a basket before you throw. If they only come up after the ball has left your hand, timing is the piece to work on.',
      'Keep a simple count of ten tries at one fixed distance so a change is visible. If the count stays flat over a couple of weeks, shorten the distance rather than repeating the same throw.',
    ],
    sortOrder: 8,
  },
  {
    _id: G('agility'),
    domainId: grossMotorDomain._id,
    domainKey: 'grossMotor',
    key: 'agility',
    name: 'Agility',
    description:
      'Running, stopping, turning and weaving — changing direction and speed on purpose, and holding a sequence of movements in mind.',
    levelDescriptors: [
      {
        level: 1,
        descriptor:
          'Runs in a straight line and stops by running out of room or into something, rather than on a signal.',
        exemplars: [
          'Runs down the hall and stops at the wall',
          'Turns by looping wide rather than cutting',
        ],
      },
      {
        level: 2,
        descriptor:
          'Runs and stops when you call stop, and walks around obstacles set in her path.',
        exemplars: [
          'Freezes on "stop" within a step or two',
          'Steps round the cushions rather than over them',
        ],
      },
      {
        level: 3,
        descriptor:
          'Runs and changes direction round cones, and works through a simple three or four station course in order.',
        exemplars: [
          'Did the timed agility courses at 2.5 years',
          'Remembers the order of four stations after one demonstration',
        ],
      },
      {
        level: 4,
        descriptor:
          'Weaves through a line of cones, shuffles sideways, and completes an eight station course remembering the order without prompting.',
        exemplars: [
          'Completed a timed eight-station obstacle course',
          'Shuffles sideways along a line without crossing her feet',
        ],
      },
      {
        level: 5,
        descriptor:
          'Runs a course faster on a second attempt while keeping the movements clean, and designs a course of her own and explains its rules.',
        exemplars: [
          'Beats her own time on a second run through the same course',
          'Sets up cushions and cones and tells you the order to do them in',
        ],
      },
    ],
    typicalMonths: [36, 48],
    prerequisites: [G('jumping'), G('balance')],
    assessmentHints: [
      'Build a course out of cushions, chairs and tape and let her run it twice. The second run tells you more than the first.',
      'Play stop, go and turn on your call. Reacting to a signal is a different skill from moving fast.',
      'Ask her to design the course and explain the rules to you. Designing it takes the same thinking as her logic work.',
    ],
    sortOrder: 9,
  },
  {
    _id: G('gymnastics'),
    domainId: grossMotorDomain._id,
    domainKey: 'grossMotor',
    key: 'gymnastics',
    name: 'Gymnastics',
    description:
      'Controlled body shapes and rolls — knowing where the body is in space and holding it in a chosen position.',
    levelDescriptors: [
      {
        level: 1,
        descriptor:
          'Rolls sideways along the floor like a log and gets from lying to standing on her own.',
        exemplars: [
          'Log-rolls across a mat laughing',
          'Pushes up to standing from the floor without a hand',
        ],
      },
      {
        level: 2,
        descriptor:
          'Holds a simple shape for a moment — a ball, a bridge, a star — and does a forward roll with hands-on help.',
        exemplars: [
          'Curls into a tight ball when you say "make a ball"',
          'Rolls over with your hands guiding her hips',
        ],
      },
      {
        level: 3,
        descriptor:
          'Bear walks and crab walks across a room, and does a forward roll with a small assist at the tuck.',
        exemplars: [
          'Gymnastics introduced at 2.75 years — rolls, bear walk, crab walk',
          'Bear walks the length of the mat with straight legs',
        ],
      },
      {
        level: 4,
        descriptor:
          'Does a forward roll on her own, tucking her chin so she rolls over her shoulders, and holds a shape steady for several seconds.',
        exemplars: [
          'Rolls over and comes up onto her feet without a hand',
          'Holds a bridge while you count to five',
        ],
      },
      {
        level: 5,
        descriptor:
          'Holds a handstand against a wall with a spot, links two moves into one sequence, and copies a short routine you show her.',
        exemplars: [
          'Walks her feet up the wall into a supported handstand',
          'Does roll, stand, jump, turn as one sequence after seeing it once',
        ],
      },
    ],
    typicalMonths: [42, 54],
    prerequisites: [G('balance')],
    assessmentHints: [
      'A soft mat or a bed is all the equipment needed. Ask for shapes by name — a ball, a bridge, a table, a star.',
      'Watch for the chin tucking in a forward roll. That is what turns a head-first tumble into a real roll.',
      'Show her a two-move sequence and ask her to copy it. Remembering the order is as much the skill as the moves.',
    ],
    sortOrder: 10,
  },
  {
    _id: G('jumpRope'),
    domainId: grossMotorDomain._id,
    domainKey: 'grossMotor',
    key: 'jumpRope',
    name: 'Jump Rope',
    description:
      'Jumping in time with a turning rope. It puts a reliable two-foot jump together with timing to something outside her own body, which is why it arrives late.',
    levelDescriptors: [
      {
        level: 1,
        descriptor:
          'Steps or jumps over a rope lying still on the ground, and walks along it like a line.',
        exemplars: [
          'Walks over the rope on the floor as part of a course',
          'Steps over one foot at a time rather than jumping',
        ],
      },
      {
        level: 2,
        descriptor:
          'Jumps over a rope you wiggle slowly along the ground, side to side like a snake.',
        exemplars: [
          'Jump rope introduced at 2.75 years',
          'Times a two-foot jump over the wiggling "snake"',
        ],
      },
      {
        level: 3,
        descriptor:
          'Jumps over a rope swung slowly over her head and down to her feet by two adults, timing a single jump to it.',
        exemplars: [
          'Clears one slow swing of a long rope turned by two people',
          'Watches the rope rather than her feet as it comes down',
        ],
      },
      {
        level: 4,
        descriptor:
          'Holds her own rope, swings it over her head and steps or jumps over it, several times one at a time.',
        exemplars: [
          'Swings and jumps four separate times in a row, resetting between each',
          'Gets the rope all the way over her head without it catching',
        ],
      },
      {
        level: 5,
        descriptor:
          'Turns her own rope and jumps it five or more times in a row without stopping.',
        exemplars: [
          'Five continuous jumps with her own rope',
          'Keeps a steady rhythm rather than resetting between jumps',
        ],
      },
    ],
    typicalMonths: [54, 72],
    prerequisites: [G('jumping')],
    assessmentHints: [
      'Start with the rope lying still, then a slow snake wiggle on the ground. Overhead comes much later.',
      'A long rope turned by two people gives her far more time than her own short rope does.',
      'Count out loud as the rope turns. The count gives her the beat, and the beat is what the timing hangs on.',
    ],
    sortOrder: 11,
  },
]

// ─────────────────────────────────────────────────────────────
// Aggregate
// ─────────────────────────────────────────────────────────────

/** Every skill defined in this file, in domain then sortOrder order. */
export const mathMotorSkills: Skill[] = [
  ...mathSkills,
  ...fineMotorSkills,
  ...grossMotorSkills,
]
